import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { makeCorePatchRequest, makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { FormControlLabel, Grid, Switch } from '@mui/material'
import { Input } from 'components/core-ui/Input'
import { useLocationsQuery } from '../../api'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Button } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toStorePayload } from './serializers'

const LOCATIONS_LIST_URL =
  '/iam/external-partners/locations?fields=locationAddressDetails,city,county&size=1000'

const getLocationDetailsUrl = (locationId: number) =>
  `/iam/external-partners/locations?size=1000&fields=city,county,locationSubtype,locationAddressDetails,metiCode,locationStoreCodes&filterBy=id&filterValue=${locationId}`

const NewStoreModal = props => {
  const {
    isModalOpen,
    closeModal,
    editItem,
    refetchStore,
    setShouldRefreshData,
    onSuccess,
    selectStore
  } = props

  const [storesOptions, setStoresOptions] = useState([])
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)

  const { showNotification } = useNotification()

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toStorePayload(values, editItem))
    }
  })

  const mutateStore = async values => {
    return editItem
      ? await makeCorePatchRequest(`/iam/external-partners/stores/${editItem.id}`, values)
      : await makeCorePostRequest('/iam/external-partners/stores', values)
  }

  const { isLoading, mutate } = useMutation(mutateStore, {
    onSuccess: res => {
      closeModal()
      formik.resetForm()
      refetchStore && refetchStore()
      setShouldRefreshData && setShouldRefreshData(true)
      onSuccess && onSuccess(res)
      selectStore && selectStore(res)
      showNotification({
        message: `Store was ${editItem?.id ? 'edited' : 'created'} successfully.`,
        type: 'success'
      })
    },
    onError: error => handleQueryError(error, '', showNotification)
  })

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
    setSelectedLocationId(null)
  }

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedLocationId(null)
    }
  }, [isModalOpen])

  const { data: storesData } = useLocationsQuery(showNotification, LOCATIONS_LIST_URL, {
    enabled: isModalOpen
  })

  const { data: locationDetailsData } = useLocationsQuery(
    showNotification,
    selectedLocationId ? getLocationDetailsUrl(selectedLocationId) : '',
    { enabled: isModalOpen && !!selectedLocationId }
  )

  useEffect(() => {
    if (storesData) {
      setStoresOptions(
        storesData.externalLocations.map(store => ({
          label: `${store.name} - ${store.city}, ${store.county}`,
          value: store.locationId,
          locationName: `${store.city}, ${store.county}`,
          storeCode: '',
          storeType: ''
        }))
      )
    }
  }, [storesData])

  useEffect(() => {
    const location = locationDetailsData?.externalLocations?.[0]
    if (!location || location.locationId !== selectedLocationId) {
      return
    }
    formik.setFieldValue('store', {
      ...formik.values.store,
      locationName: `${location.city}, ${location.county}`,
      storeCode: location.metiCode || '',
      storeType: location.locationSubtype || ''
    })
  }, [locationDetailsData, selectedLocationId])

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle="Store Details"
      modalWidth="50%"
    >
      <form>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <AsyncAutocomplete
              variant="outlined"
              name="store"
              label="Store name (Assign new store to management mandate)"
              value={formik.values.store}
              options={storesOptions}
              onChange={value => {
                formik.setFieldValue('store', value)
                setSelectedLocationId(value?.value || null)
              }}
              required
              disabled={editItem}
              error={!!formik.errors.store}
              helperText={
                typeof formik.errors.store === 'object' && !Array.isArray(formik.errors.store)
                  ? (formik.errors.store?.value as string)
                  : ''
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Input
                  type="text"
                  variant="outlined"
                  name="locationName"
                  label="Location"
                  width="100%"
                  value={formik.values.store?.locationName}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  type="text"
                  variant="outlined"
                  name="storeCode"
                  label="METI Code"
                  width="100%"
                  value={formik.values.store?.storeCode}
                  required
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  type="text"
                  variant="outlined"
                  name="storeType"
                  label="Store Type"
                  width="100%"
                  value={formik.values.store?.storeType}
                  required
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
          {editItem?.id && (
            <Grid item xs={12}>
              <span style={{ display: 'inline-block', lineHeight: '38px', marginRight: '12px' }}>
                Status:
              </span>
              <FormControlLabel
                control={
                  <Switch
                    name="isEnabled"
                    checked={formik.values.isEnabled}
                    onChange={event => formik.setFieldValue('isEnabled', event.target.checked)}
                    onBlur={formik.handleBlur}
                  />
                }
                label="Enabled"
              />
              <span
                style={{
                  color: 'rgba(0, 0, 0, 0.38)',
                  display: 'inline-block',
                  lineHeight: '38px'
                }}
              >
                (can be disabled if no active assigned partners)
              </span>
            </Grid>
          )}
          <Grid item xs={12} className={s.modalButtons}>
            <Button variant="outlined" label="Cancel" onClick={cancelModal} />
            <Button
              variant="contained"
              label="Save"
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </form>
    </Modal>
  )
}

export default NewStoreModal
